import type { TextInputEventValue } from '@interstate/components/TextInput';
import { TextInput } from '@interstate/components/TextInput';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { formatUtils, kebabCase } from '@makemydeal/dr-common-utils';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { TaxOverrideFieldInputContainer } from './TaxOverrideField.style';
import { dealLockHooks, TOTAL_TAX } from '@makemydeal/dr-dash-components';
import { convertToNumber } from '../../../utils/formatUtils';

const InputLabelStyle = {
    '.ids-input-component-label': {
        justifyContent: 'end !important'
    }
};
type TaxTotalOverrideFieldProps = {
    setTaxOverrideFormEdited: (taxFormEdited: boolean) => void;
    taxOverrideFormEdited: boolean;
};

const MAXIMUM_ALLOWED_VALUE = 999999.99;
const MINIMUM_ALLOWED_VALUE = 0;

const TaxTotalOverrideField = (props: TaxTotalOverrideFieldProps) => {
    const dispatch = useDispatch();

    const totalTax = useSelector(offerReduxSelectors.getTotalTax);
    const manualTotalTaxEdited = useSelector(offerReduxSelectors.getManualTotalTaxEdited);
    const formattedTotalTax = formatUtils.formatDollarsAndCents(totalTax);
    const useManualTotalTaxEditedFlag = useSelector(featureToggleSelectors.useManualTotalTaxEdited);
    const [inputText, setInputText] = useState(formattedTotalTax);
    const prevValueBeforeBlur = useRef<string>(inputText || '');
    const isBlurInProgress = useRef(false);
    const isTotalTaxEdited = useManualTotalTaxEditedFlag ? manualTotalTaxEdited : props.taxOverrideFormEdited;
    const isLocked = dealLockHooks.useIsDealLocked();

    const handleInputChanged = (event: any) => {
        !useManualTotalTaxEditedFlag && props.setTaxOverrideFormEdited(true);
        const taxOverrideValue = event.target.value;
        if (taxOverrideValue !== inputText) {
            const numericTotalTaxOverride = parseFloat(taxOverrideValue.replace(/[$,]/g, ''));
            if (numericTotalTaxOverride < MINIMUM_ALLOWED_VALUE || numericTotalTaxOverride > MAXIMUM_ALLOWED_VALUE) {
                return;
            }
            setInputText(taxOverrideValue);
        }
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }
        dispatch(
            offerActionCreators.updatePaymentCallPendingState(
                convertToNumber(event.target.value) !== convertToNumber(prevValueBeforeBlur.current)
            )
        );
    };

    const processOverrideValue = (rawValue: string) => {
        const numericTotalTaxOverride = convertToNumber(rawValue);

        if (numericTotalTaxOverride !== convertToNumber(prevValueBeforeBlur.current)) {
            if (numericTotalTaxOverride < MINIMUM_ALLOWED_VALUE || numericTotalTaxOverride > MAXIMUM_ALLOWED_VALUE) {
                return;
            }
            prevValueBeforeBlur.current = rawValue;
            setInputText(formatUtils.formatDollarsAndCents(numericTotalTaxOverride));
            initiateTaxOverridePaymentCall(numericTotalTaxOverride);
        }
    };

    const handleBlur = (event: InterstateOnChangeEvent<TextInputEventValue>) => {
        isBlurInProgress.current = true;
        processOverrideValue(event.target.value);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            processOverrideValue(event.currentTarget.value);
        }
    };

    /* istanbul ignore next */
    const initiateTaxOverridePaymentCall = (overrideValue: number) => {
        dispatch(offerActionCreators.updatedTotalTaxOverride(overrideValue, useManualTotalTaxEditedFlag));
        const formattedTotalTax = formatUtils.formatDollarsAndCents(overrideValue);
        setInputText(formattedTotalTax);
    };

    return (
        <TaxOverrideFieldInputContainer $taxOverrideFormEdited={isTotalTaxEdited}>
            <TextInput
                sx={InputLabelStyle}
                name={kebabCase(TOTAL_TAX)}
                label={TOTAL_TAX}
                value={inputText}
                onChange={handleInputChanged}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                data-testid={kebabCase(TOTAL_TAX)}
                size="sm"
                disabled={isLocked}
            />
        </TaxOverrideFieldInputContainer>
    );
};
export default TaxTotalOverrideField;
